import React from 'react';
import { dashboard, invoices, contracts, currency, formatDate, getClientName } from '../data';

const statusToBadge: Record<string, string> = {
  Paid: 'badge-paid',
  Issued: 'badge-issued',
  Pending: 'badge-pending',
  Overdue: 'badge-overdue',
  Upcoming: 'badge-pending',
};

type DashboardProps = {
  onSelectContract: (id: string) => void;
  onSelectInvoice: (id: string) => void;
};

export const Dashboard: React.FC<DashboardProps> = ({ onSelectContract, onSelectInvoice }) => {
  const openInvoices = invoices
    .filter((invoice) => invoice.status !== 'Paid')
    .slice(0, 4);

  const rollingContractValue = contracts.reduce((sum, contract) => sum + contract.value, 0);

  return (
    <div className="d-flex flex-column gap-4">
      <section className="row g-3">
        <div className="col-md-3">
          <div className="kpi-card">
            <p className="text-muted mb-1">Total contract value</p>
            <h3 className="h4 mb-0">{currency(rollingContractValue)}</h3>
            <small className="text-success">↑ 12% vs last quarter</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="kpi-card">
            <p className="text-muted mb-1">Issued last month</p>
            <h3 className="h4 mb-0">{currency(dashboard.lastMonth.issued)}</h3>
            <small className="text-muted">Collected: {currency(dashboard.lastMonth.collected)}</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="kpi-card">
            <p className="text-muted mb-1">Outstanding</p>
            <h3 className="h4 mb-0">{currency(dashboard.lastMonth.outstanding)}</h3>
            <small className="text-danger">Flag overdue within 5 days</small>
          </div>
        </div>
        <div className="col-md-3">
          <div className="kpi-card">
            <p className="text-muted mb-1">Next 30 days</p>
            <h3 className="h4 mb-0">
              {currency(
                dashboard.forecast.reduce((sum, slot) => sum + slot.expected, 0) /
                  dashboard.forecast.length,
              )}
            </h3>
            <small className="text-muted">Avg monthly projection</small>
          </div>
        </div>
      </section>

      <section className="row g-4">
        <div className="col-lg-7">
          <div className="section-card h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h2 className="h5 mb-0">Forecast runway</h2>
                <small className="text-muted">Forward-looking invoice projection</small>
              </div>
              <span className="badge bg-light text-dark">Next quarter</span>
            </div>
            <div className="d-flex flex-column gap-3">
              {dashboard.forecast.map((slot) => (
                <div key={slot.month}>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-semibold">{slot.month}</span>
                    <span>{currency(slot.expected)}</span>
                  </div>
                  <div className="progress" style={{ height: '0.6rem' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${(slot.expected / 200000) * 100}%` }}
                      aria-valuenow={slot.expected}
                      aria-valuemin={0}
                      aria-valuemax={200000}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="section-card h-100">
            <h2 className="h5 mb-3">Alerts & follow-ups</h2>
            <div className="d-flex flex-column gap-3">
              {dashboard.alerts.map((alert) => (
                <div key={alert.id} className="alert-card p-3 rounded">
                  <p className="text-uppercase text-muted small mb-1">{alert.title}</p>
                  <h3 className="h6 mb-1">{alert.project}</h3>
                  <p className="mb-0 text-muted">{alert.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-card">
        <div className="d-flex justify-content-between mb-3">
          <div>
            <h2 className="h5 mb-0">Upcoming invoices</h2>
            <small className="text-muted">Monitor readiness and notes</small>
          </div>
          <button className="btn btn-sm btn-outline-secondary">Export CSV</button>
        </div>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th scope="col">Invoice</th>
                <th scope="col">Client</th>
                <th scope="col">Amount</th>
                <th scope="col">Due date</th>
                <th scope="col">Status</th>
                <th scope="col">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {openInvoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="clickable-row"
                  onClick={() => onSelectInvoice(invoice.id)}
                >
                  <td className="fw-semibold">{invoice.id}</td>
                  <td>{invoice.clientName}</td>
                  <td>{currency(invoice.amount)}</td>
                  <td>{formatDate(invoice.dueDate)}</td>
                  <td>
                    <span className={`invoice-pill ${statusToBadge[invoice.status]}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="text-muted">{invoice.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="row g-4">
        {contracts.slice(0, 2).map((contract) => (
          <div className="col-lg-6" key={contract.id}>
            <div className="section-card h-100">
              <div className="d-flex justify-content-between mb-2">
                <div>
                  <p className="text-uppercase text-muted small mb-1">{getClientName(contract.clientId)}</p>
                  <h3 className="h5 mb-0">{contract.name}</h3>
                </div>
                <span className="badge bg-light text-dark">{contract.status}</span>
              </div>
              <p className="text-muted mb-2">Owner: {contract.manager}</p>
              <ul className="list-unstyled mb-0">
                {contract.paymentTerms.map((term) => (
                  <li key={term.label} className="d-flex align-items-center mb-2">
                    <span className="timeline-dot me-3" />
                    <div>
                      <div className="fw-semibold">{term.label}</div>
                      <small className="text-muted">
                        {currency(term.amount)} • Due {formatDate(term.dueDate)} • {term.milestone}
                      </small>
                    </div>
                    <span className={`ms-auto invoice-pill ${term.status === 'Completed' ? 'badge-paid' : 'badge-pending'}`}>
                      {term.status}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                className="btn btn-link p-0 mt-3"
                type="button"
                onClick={() => onSelectContract(contract.id)}
              >
                View contract →
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
};
